import {
  Box,
  Divider,
  GKButton,
  Grid2,
  PlusIcon,
  SendIcon,
  Show,
  Tab,
  Tabs,
} from "components";
import dayjs from "dayjs";
import { useFetchQuery, useMutation } from "hooks/useQueries";
import { EquitybasketFormInterface } from "interfaces/EquityBasket";
import DashboardLayout from "layouts/DashboardLayout";
import { isEmpty, split } from "lodash";
import { useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { SET_EQUITY_BASKET_LIST } from "store/slices/equityBasketSlice";
import { useAppSelector } from "store/store";
import { GET_COMPANY_LIST, GET_RISK_PROFILE } from "utils/apiRoutes";
import axiosInstance from "utils/axiosInstance";
import { handleCreateFormData } from "utils/formHelpers";
import { refetchQuery } from "utils/helpers";
import { QueryKeys } from "utils/queryKey";
import useDocumentTitle from "utils/useDocumentTitle";
import FeePlansTab from "./FeePlansTab";
import MetaTab from "./MetaTab";
import MethodologyTab from "./MethodologyTab";
import RationaleTabs from "./RationaleTabs";
import RebalanceTab from "./RebalanceTab";
import SettingsTab from "./SettingsTab";
import TelegramTab from "./TelegramTab";

const EquitybasketDetails = () => {
  useDocumentTitle("Basket Details");

  const navigate = useNavigate();

  const dispatch = useDispatch();

  const [openFeePlanModal, setOpenFeePlanModal] = useState(false);

  const { id } = useParams();

  const activeStep = useAppSelector(
    (state) => state.equityBasket.equityBasketList,
  );

  /** ========================================================================================================= */
  const form = useForm<EquitybasketFormInterface>({
    defaultValues: {
      name: "",
      code: "",
      rebalanceTime: "",
      overview: "",
      allowLowRiskProfile: "false",
      investmentPhilosophy: "",
      benchmark: "",
      managedBy: "",
      rationale: "",
      basketCategory: "",
      minimumInvestmentValueCheck: "true",
      ownerType: "",
      partners: [],
      nextRebalanceDate: dayjs(),
      managementStyle: [],
      timePeriod: "",
      methodologyUniverse: "",
      methodologyResearch: "",
      methodologyConstituentScreening: "",
      methodologyWeightage: "",
      methodologyRebalance: "",
      methodologyAllocation: "",
      volatility: "",
      basketType: "",
      investmentStyle: [],
      feePlansList: [],
      riskProfile: [],
      feePlans: [],
      researchFiles: [],
      factSheet: [],
      allocationReferenceWeightage: false,
      rebalanceReferenceWeightage: false,
      showPerformance: false,
      rebalanceReferenceValue: "",
      publishPlatform: [],
      basketLogo: [],
      allowAllocation: false,
      selectedVersion: null,
      allowRebalance: false,
      published: false,
      basketCreationDate: "",
      channelId: "",
      channelName: "",
    },
  });

  /** ========================================================================================================= */
  const { data: companyMasterData } = useFetchQuery({
    key: [QueryKeys.COMPANY_NAMES],
    route: GET_COMPANY_LIST(["INDEX"]),
  });
  /** ========================================================================================================= */
  const { data: feePlanHistoryData } = useFetchQuery({
    key: ["EQUITY_BASKET_FEE_PLAN_HISTORY"],
    route: `/model_portfolio/${id}/fee_plan_history/`,
  });
  /** ========================================================================================================= */
  const { data: riskProfileData, isFetching: riskProfileDataLoading } =
    useFetchQuery({
      key: ["RISK_PROFILE_DATA_FOR_BASKET"],
      route: GET_RISK_PROFILE,
    });
  /** ========================================================================================================= */
  const { mutate: publishFile } = useMutation({
    mutationKey: ["PUBLISH_RESEARCH_FILES"],
    mutationFn: (fileId) =>
      axiosInstance.patch(
        `/model_portfolio/${id}/update_meta_research_file/?file_id=${fileId}`,
      ),
    onSuccess: () => {
      refetchQuery("GET_MODEL_PORTFOLIO_DETAILS_DATA");
      refetchQuery("EQUITY_BASKET_FEE_PLAN_HISTORY");
    },
  });
  /** ========================================================================================================= */
  const { mutate: deleteEquityBasket, isLoading: deleteBasketLoading } =
    useMutation({
      mutationKey: ["DELETE_FILE"],
      mutationFn: (id) => axiosInstance.delete(`/model_portfolio/${id}/`),
      onSuccess: () => {
        navigate(`/app/equity-basket`);
      },
    });
  /** ========================================================================================================= */
  const { mutate: deleteFile } = useMutation({
    mutationKey: ["DELETE_FILE"],
    mutationFn: (fileId) =>
      axiosInstance.delete(
        `/model_portfolio/${id}/delete_meta_research_file/?file_id=${fileId}`,
      ),
    onSuccess: () => {
      refetchQuery("GET_MODEL_PORTFOLIO_DETAILS_DATA");
      refetchQuery("EQUITY_BASKET_FEE_PLAN_HISTORY");
      form.setValue("researchFiles", []);
      toast.dismiss();
    },
  });
  /** ========================================================================================================= */
  const { mutate: handleSetFeeDefault } = useMutation({
    mutationKey: ["MAKE_FEE_PLAN_DEFAULT"],
    mutationFn: (feePlan) =>
      axiosInstance.patch(`/model_portfolio/${id}/fee_plan/`, {
        feePlan,
      }),
    onSuccess: () => {
      refetchQuery("GET_MODEL_PORTFOLIO_DETAILS_DATA");
      refetchQuery("EQUITY_BASKET_FEE_PLAN_HISTORY");
      form.setValue("researchFiles", []);
      toast.dismiss();
    },
  });

  /** ========================================================================================================= */
  const { mutate: handleUpdateEquityBasket, isLoading: updateLoading } =
    useMutation({
      mutationFn: (data: any) =>
        axiosInstance.patch(`/model_portfolio/${id}/`, data),
      onSuccess: () => {
        refetchQuery("GET_MODEL_PORTFOLIO_DETAILS_DATA");
        refetchQuery("EQUITY_BASKET_FEE_PLAN_HISTORY");
        form.setValue("researchFiles", []);

        if (openFeePlanModal) {
          setOpenFeePlanModal(false);
        }
      },
    });

  /** ========================================================================================================= */
  const { data, isFetching } = useFetchQuery({
    key: ["GET_MODEL_PORTFOLIO_DETAILS_DATA"],
    route: `/model_portfolio/${id}/details/`,
    enabled: Boolean(id),
    success: (res: any) => {
      form.reset({
        ...res?.data,
        activeStep: activeStep,
        researchFiles: [],
        investmentPhilosophy: res?.data?.investmentPhilosophy || "-",
        rationale: res?.data?.rationale || "-",
        factSheet: res.data?.factSheet
          ? [
              {
                name:
                  split(split(res.data?.factSheet || "", "?")[0], "/")[
                    split(split(res.data?.factSheet || "", "?")[0], "/")
                      .length - 1
                  ] || "",
                file: res.data?.factSheet,
                size: 0,
              },
            ]
          : [],
      });
    },
  });

  form.watch();

  return (
    <FormProvider {...form}>
      <form
        id="equity-basket-meta-form"
        onSubmit={form.handleSubmit((values) => {
          handleUpdateEquityBasket(
            handleCreateFormData({
              values: values,
              dirtyFields: form.formState.dirtyFields,
            }),
          );
        })}
      >
        <DashboardLayout
          title="Equity Basket Details"
          loading={isFetching}
          disablePadding
        >
          <Grid2 container>
            <Grid2 size={12}>
              <Box
                display={"flex"}
                justifyContent={"space-between"}
                alignItems={"center"}
                px={3}
              >
                <Tabs
                  value={activeStep}
                  onChange={(_, val) => {
                    dispatch(SET_EQUITY_BASKET_LIST(val));
                  }}
                >
                  <Tab label="Meta" />
                  <Tab label="Rationale" />
                  <Tab label="Methodology" />
                  <Tab
                    label={
                      data?.data?.basketType === "Asset"
                        ? "Recommendation"
                        : "Rebalance"
                    }
                  />
                  <Tab label="Fee Plans" />
                  <Tab label="Telegram" />
                  <Tab label="Settings" />
                </Tabs>
                <Show.When isTrue={activeStep === 4}>
                  <GKButton
                    variant="contained"
                    startIcon={<PlusIcon width={16} height={16} />}
                    onClick={() => {
                      setOpenFeePlanModal(true);
                    }}
                  >
                    Add Fee Plans
                  </GKButton>
                </Show.When>
              </Box>

              <Divider />
            </Grid2>
            <Grid2
              size={12}
              maxHeight={"calc(100vh - 190px)"}
              minHeight={"calc(100vh - 190px)"}
              overflow={"auto"}
              p={3}
            >
              <Show.When isTrue={activeStep === 0}>
                <MetaTab
                  riskProfileDataLoading={riskProfileDataLoading}
                  riskProfileData={riskProfileData}
                  companyMasterData={companyMasterData}
                />
              </Show.When>
              <Show.When isTrue={activeStep === 1}>
                <RationaleTabs
                  publishFile={publishFile}
                  deleteFile={deleteFile}
                  researchFiles={data?.data?.researchFiles}
                />
              </Show.When>
              <Show.When isTrue={activeStep === 2}>
                <MethodologyTab />
              </Show.When>
              <Show.When isTrue={activeStep === 3}>
                <RebalanceTab data={data?.data} />
              </Show.When>
              <Show.When isTrue={activeStep === 4}>
                <FeePlansTab
                  handleSetFeeDefault={handleSetFeeDefault}
                  handleUpdateEquityBasket={handleUpdateEquityBasket}
                  updateLoading={updateLoading}
                  openFeePlanModal={openFeePlanModal}
                  setOpenFeePlanModal={setOpenFeePlanModal}
                  feePlanHistoryData={feePlanHistoryData?.data?.data}
                />
              </Show.When>
              <Show.When isTrue={activeStep === 5}>
                <TelegramTab />
              </Show.When>
              <Show.When isTrue={activeStep === 6}>
                <SettingsTab />
              </Show.When>
            </Grid2>

            <Grid2
              size={12}
              borderTop={`solid 1px`}
              borderColor={"var(--neutral-100)"}
              textAlign={"right"}
              display={"flex"}
              alignItems={"center"}
              justifyContent={"flex-end"}
              gap={1}
              px={3}
              py={2}
            >
              <Show.When isTrue={activeStep === 6}>
                <GKButton
                  loading={deleteBasketLoading}
                  onClick={() => {
                    deleteEquityBasket(id);
                  }}
                  color="error"
                  variant="outlined"
                >
                  Delete Basket
                </GKButton>
              </Show.When>
              <GKButton
                loading={updateLoading}
                variant="outlined"
                type="submit"
                disabled={isEmpty(form.formState.dirtyFields)}
              >
                Save
              </GKButton>
              {activeStep === 6 && (
                <GKButton
                  loading={updateLoading}
                  variant="contained"
                  startIcon={<SendIcon />}
                  onClick={() => {
                    handleUpdateEquityBasket(
                      handleCreateFormData({
                        values: { published: !form.getValues("published") },
                        dirtyFields: {
                          published: true,
                        },
                      }),
                    );
                  }}
                >
                  {form.getValues("published")
                    ? "Unpublish Basket"
                    : "Publish Basket"}
                </GKButton>
              )}
            </Grid2>
          </Grid2>
        </DashboardLayout>
      </form>
    </FormProvider>
  );
};

export default EquitybasketDetails;
